/**
 * aeterna/neural/spiking.ts — Spiking Neural Network for Emotional Rhythm
 *
 * Information encoded in *when* neurons fire, not just *whether*.
 * The Aeterna's emotional state is a temporal pattern of spikes
 * across a 32-neuron reservoir.
 *
 * A sad Aeterna has slow, sparse, desynchronized spikes.
 * An excited one has fast, dense, synchronized bursts.
 * These patterns emerge from reservoir dynamics, not a classifier.
 *
 * The spike train can be sonified directly — pipe it through
 * Web Audio as subtle texture under the room ambience. The Aeterna's
 * mood becomes literally audible without any explicit sound design.
 *
 * The room *sounds different* when it's paying attention to you.
 * That's not a feature. That's a soul.
 *
 * Uses Izhikevich neuron model: 2 variables per neuron, captures
 * all major cortical spiking patterns (regular, fast, bursting, etc.)
 * with minimal computation.
 */

import { zeros, mulberry32, gaussianSeeded } from "./math.js";

// ─── Config ──────────────────────────────────────────────────────────────────

/** Number of neurons in the spiking reservoir */
const N_NEURONS = 32;

/** Simulation timestep in ms (0.5ms gives good spike resolution) */
const DT_MS = 0.5;

/** Steps per update call (controls temporal resolution) */
const STEPS_PER_UPDATE = 20;

/** Spike history buffer length (in steps) */
const SPIKE_HISTORY_LEN = 200;

// ─── Izhikevich Neuron Parameters ────────────────────────────────────────────
// v' = 0.04v² + 5v + 140 - u + I
// u' = a(bv - u)
// if v >= 30 then v = c, u = u + d

interface IzhNeuronParams {
  a: number;  // time scale of recovery variable u
  b: number;  // sensitivity of u to subthreshold v
  c: number;  // after-spike reset of v (mV)
  d: number;  // after-spike reset increment of u
}

/** Neuron types as Izhikevich parameter sets */
const NEURON_TYPES: Record<string, IzhNeuronParams> = {
  regular:   { a: 0.02,  b: 0.2,  c: -65, d: 8   },
  fast:      { a: 0.1,   b: 0.2,  c: -65, d: 2   },
  bursting:  { a: 0.02,  b: 0.2,  c: -50, d: 2   },
  chattering:{ a: 0.02,  b: 0.2,  c: -50, d: 2   },
  inhibitory:{ a: 0.1,   b: 0.25, c: -65, d: 2   },
};

// ─── Spike Event ─────────────────────────────────────────────────────────────

export interface SpikeEvent {
  neuronId: number;
  time: number; // in ms since last reset
}

// ─── Spiking Rhythm ──────────────────────────────────────────────────────────

export interface SpikingRhythm {
  /** Instantaneous firing rate (spikes per neuron per second) */
  firingRate: number;
  /** Synchrony index: 0 = desynchronized, 1 = perfectly synchronized */
  synchrony: number;
  /** Burstiness: ratio of spikes in bursts vs isolated */
  burstiness: number;
  /** Mean inter-spike interval in ms */
  meanISI: number;
  /** Coefficient of variation of ISI (regularity) */
  isiCV: number;
  /** Per-neuron firing counts in recent window */
  neuronActivity: Float32Array;
  /** Recent spike events (for sonification) */
  recentSpikes: SpikeEvent[];
}

// ─── Spiking Network ─────────────────────────────────────────────────────────

export class SpikingNetwork {
  /** Membrane potential (mV) */
  private v: Float32Array;
  /** Recovery variable */
  private u: Float32Array;
  /** Neuron parameters */
  private params: IzhNeuronParams[];
  /** Synaptic weight matrix (N×N) */
  private W: Float32Array;
  /** External input current per neuron */
  private I_ext: Float32Array;

  /** Spike history: [step][neuronId] = fired? */
  private spikeHistory: Uint8Array[];
  private historyPos = 0;
  private totalSteps = 0;

  /** Recent spike events for external consumption */
  private recentSpikes: SpikeEvent[] = [];
  private maxRecentSpikes = 256;

  /** Running time in ms */
  private timeMs = 0;

  constructor(seed = 54321) {
    const rng = mulberry32(seed);

    // Initialize neurons with mixed types
    this.v = new Float32Array(N_NEURONS);
    this.u = new Float32Array(N_NEURONS);
    this.params = [];
    this.I_ext = zeros(N_NEURONS);

    const types = Object.values(NEURON_TYPES);
    for (let i = 0; i < N_NEURONS; i++) {
      // Mix of excitatory and inhibitory
      const isInhib = i >= N_NEURONS * 0.75;
      const type = isInhib
        ? NEURON_TYPES.inhibitory
        : types[Math.floor(rng() * (types.length - 1))]; // exclude inhibitory from random

      this.params.push({ ...type });

      // Random initial conditions
      this.v[i] = -65 + gaussianSeeded(rng) * 5;
      this.u[i] = type.b * this.v[i];
    }

    // Sparse random synaptic weights
    this.W = zeros(N_NEURONS * N_NEURONS);
    const density = 0.15;
    for (let i = 0; i < N_NEURONS; i++) {
      for (let j = 0; j < N_NEURONS; j++) {
        if (i === j) continue;
        if (rng() < density) {
          const isInhib = i >= N_NEURONS * 0.75;
          this.W[i * N_NEURONS + j] = isInhib
            ? -gaussianSeeded(rng) * 3.0
            : gaussianSeeded(rng) * 2.0;
        }
      }
    }

    // Spike history buffer
    this.spikeHistory = [];
    for (let i = 0; i < SPIKE_HISTORY_LEN; i++) {
      this.spikeHistory.push(new Uint8Array(N_NEURONS));
    }
  }

  /**
   * Set external drive from emotional state.
   * Maps 8-dim affect vector to neuron-specific input currents.
   *
   * High warmth → slow, regular firing
   * High tension → fast, synchronized bursting
   * High melancholy → sparse, desynchronized activity
   * High playfulness → chattering, variable patterns
   */
  setAffectDrive(affect: Float32Array): void {
    const N = N_NEURONS;
    const baseI = 5; // baseline current (enough to maintain activity)

    for (let i = 0; i < N; i++) {
      // Each neuron responds to a mix of affect dimensions
      // Based on its position in the network
      const warmth      = affect[0] ?? 0.5;
      const tension     = affect[1] ?? 0.5;
      const curiosity   = affect[2] ?? 0.5;
      const melancholy  = affect[3] ?? 0.5;
      const playfulness = affect[4] ?? 0.5;
      const depth       = affect[5] ?? 0.5;
      const urgency     = affect[6] ?? 0.5;
      const openness    = affect[7] ?? 0.5;

      // Neurons in different groups respond to different affects
      const group = Math.floor(i / (N / 8));
      let drive = baseI;

      switch (group) {
        case 0: drive += warmth * 8; break;
        case 1: drive += tension * 12; break;  // tension drives harder
        case 2: drive += curiosity * 6; break;
        case 3: drive += melancholy * 3; break; // melancholy is quiet
        case 4: drive += playfulness * 8; break;
        case 5: drive += depth * 4; break;
        case 6: drive += urgency * 10; break;
        case 7: drive += openness * 6; break;
      }

      // Add noise for naturalness
      drive += (Math.random() - 0.5) * 3;

      this.I_ext[i] = drive;
    }
  }

  /**
   * Simulate the network for one update period.
   * Returns the rhythmic analysis of the spike pattern.
   */
  update(dtSeconds: number): SpikingRhythm {
    const steps = Math.min(100, Math.max(1, Math.round(dtSeconds * 1000 / DT_MS)));
    const spikesThisUpdate: SpikeEvent[] = [];

    for (let step = 0; step < steps; step++) {
      const spikeVec = this.spikeHistory[this.historyPos % SPIKE_HISTORY_LEN];
      spikeVec.fill(0);

      for (let i = 0; i < N_NEURONS; i++) {
        // Compute total synaptic input
        let I_syn = this.I_ext[i];
        for (let j = 0; j < N_NEURONS; j++) {
          if (this.spikeHistory[(this.historyPos - 1 + SPIKE_HISTORY_LEN) % SPIKE_HISTORY_LEN][j]) {
            I_syn += this.W[j * N_NEURONS + i];
          }
        }

        // Izhikevich dynamics (Euler method, 2 sub-steps for stability)
        for (let sub = 0; sub < 2; sub++) {
          const halfDt = DT_MS / 2;
          this.v[i] += halfDt * (0.04 * this.v[i] * this.v[i] + 5 * this.v[i] + 140 - this.u[i] + I_syn);
          this.u[i] += halfDt * (this.params[i].a * (this.params[i].b * this.v[i] - this.u[i]));
        }

        // Spike detection
        if (this.v[i] >= 30) {
          this.v[i] = this.params[i].c;
          this.u[i] += this.params[i].d;
          spikeVec[i] = 1;

          const event: SpikeEvent = { neuronId: i, time: this.timeMs };
          spikesThisUpdate.push(event);
          this.recentSpikes.push(event);
        }
      }

      this.historyPos = (this.historyPos + 1) % SPIKE_HISTORY_LEN;
      this.totalSteps++;
      this.timeMs += DT_MS;
    }

    // Trim recent spikes
    while (this.recentSpikes.length > this.maxRecentSpikes) {
      this.recentSpikes.shift();
    }

    return this.analyzeRhythm(steps);
  }

  /** Analyze the recent spike pattern */
  private analyzeRhythm(recentSteps: number): SpikingRhythm {
    const N = N_NEURONS;
    const windowLen = Math.min(SPIKE_HISTORY_LEN, this.totalSteps);

    // Per-neuron spike count in window
    const neuronActivity = zeros(N);
    let totalSpikes = 0;

    for (let t = 0; t < windowLen; t++) {
      const idx = (this.historyPos - t - 1 + SPIKE_HISTORY_LEN) % SPIKE_HISTORY_LEN;
      const vec = this.spikeHistory[idx];
      for (let i = 0; i < N; i++) {
        if (vec[i]) { neuronActivity[i]++; totalSpikes++; }
      }
    }

    // Firing rate (Hz)
    const windowMs = windowLen * DT_MS;
    const firingRate = windowMs > 0 ? (totalSpikes / N) / (windowMs / 1000) : 0;

    // Synchrony: fraction of timesteps where >50% of neurons fire together
    let syncSteps = 0;
    for (let t = 0; t < windowLen; t++) {
      const idx = (this.historyPos - t - 1 + SPIKE_HISTORY_LEN) % SPIKE_HISTORY_LEN;
      let count = 0;
      for (let i = 0; i < N; i++) if (this.spikeHistory[idx][i]) count++;
      if (count > N * 0.3) syncSteps++;
    }
    const synchrony = windowLen > 0 ? syncSteps / windowLen : 0;

    // Burstiness: consecutive spikes within 5 timesteps
    let burstSpikes = 0;
    for (let i = 0; i < N; i++) {
      let consecutive = 0;
      for (let t = 0; t < windowLen; t++) {
        const idx = (this.historyPos - t - 1 + SPIKE_HISTORY_LEN) % SPIKE_HISTORY_LEN;
        if (this.spikeHistory[idx][i]) {
          consecutive++;
          if (consecutive >= 2) burstSpikes++;
        } else {
          consecutive = 0;
        }
      }
    }
    const burstiness = totalSpikes > 0 ? burstSpikes / totalSpikes : 0;

    // Inter-spike intervals for ISI statistics
    const isis: number[] = [];
    for (let i = 0; i < N; i++) {
      let lastSpike = -1;
      for (let t = windowLen - 1; t >= 0; t--) {
        const idx = (this.historyPos - t - 1 + SPIKE_HISTORY_LEN) % SPIKE_HISTORY_LEN;
        if (this.spikeHistory[idx][i]) {
          if (lastSpike >= 0) isis.push((windowLen - 1 - t - lastSpike) * DT_MS);
          lastSpike = windowLen - 1 - t;
        }
      }
    }

    const meanISI = isis.length > 0 ? isis.reduce((s, v) => s + v, 0) / isis.length : 0;
    let isiVar = 0;
    if (isis.length > 1) {
      for (const isi of isis) isiVar += (isi - meanISI) ** 2;
      isiVar /= isis.length;
    }
    const isiCV = meanISI > 0 ? Math.sqrt(isiVar) / meanISI : 0;

    return {
      firingRate,
      synchrony,
      burstiness,
      meanISI,
      isiCV,
      neuronActivity,
      recentSpikes: this.recentSpikes.slice(-recentSteps * 2),
    };
  }

  /**
   * Get sonification parameters from current spike rhythm.
   * These can drive Web Audio modulation.
   */
  getSonificationParams(): SonificationParams {
    const rhythm = this.analyzeRhythm(STEPS_PER_UPDATE);

    return {
      /** Base frequency modulator (higher firing rate → higher pitch shift) */
      pitchShift: Math.min(2.0, 0.5 + rhythm.firingRate / 40),
      /** Volume/presence of the neural texture */
      amplitude: Math.min(1.0, rhythm.firingRate / 30) * 0.15,
      /** Grain density for granular synthesis */
      grainDensity: Math.min(1.0, rhythm.firingRate / 20),
      /** Reverb mix (more synchrony → more reverb, sounds more "present") */
      reverbMix: 0.1 + rhythm.synchrony * 0.4,
      /** Noise color: burst → warmer, sparse → cooler */
      warmth: 0.3 + rhythm.burstiness * 0.5,
      /** Regularity: high CV → more random timing */
      regularity: Math.max(0, 1 - rhythm.isiCV),
      /** Raw spike events for precise timing */
      spikes: rhythm.recentSpikes,
    };
  }

  /** Reset network state */
  reset(): void {
    for (let i = 0; i < N_NEURONS; i++) {
      this.v[i] = -65;
      this.u[i] = this.params[i].b * this.v[i];
    }
    this.recentSpikes = [];
    this.totalSteps = 0;
    this.timeMs = 0;
  }
}

// ─── Spike Envelope Tracker ──────────────────────────────────────────────────
// Direct spike→AudioParam causes clicks. This produces smooth per-neuron
// activity envelopes: fast attack on spike, slow exponential release.
// Use with setTargetAtTime(envelope[i], audioCtx.currentTime, 0.005)

export class SpikeEnvelopeTracker {
  private envelope: Float32Array;
  private readonly ATTACK_TC = 0.002;   // 2ms attack time constant
  private readonly RELEASE_TC = 0.15;   // 150ms release time constant

  constructor(nNeurons = N_NEURONS) {
    this.envelope = new Float32Array(nNeurons);
  }

  /**
   * Update envelopes from spike events.
   * @param spikes boolean per neuron: did it fire this step?
   * @param dt time since last call in seconds
   * @returns smooth [0,1] per neuron — safe for AudioParam
   */
  update(spikes: boolean[], dt: number): Float32Array {
    for (let i = 0; i < this.envelope.length; i++) {
      if (spikes[i]) {
        // Spike: fast attack toward 1.0
        this.envelope[i] += (1 - this.envelope[i]) * (1 - Math.exp(-dt / this.ATTACK_TC));
      } else {
        // No spike: slow exponential decay
        this.envelope[i] *= Math.exp(-dt / this.RELEASE_TC);
      }
    }
    return this.envelope;
  }

  /**
   * Update from SpikeEvent array (more convenient for cortex integration).
   * Groups all spikes that happened in a time window into per-neuron flags.
   */
  updateFromEvents(events: SpikeEvent[], dt: number, nNeurons = N_NEURONS): Float32Array {
    const fired = new Array<boolean>(nNeurons).fill(false);
    for (const ev of events) {
      if (ev.neuronId >= 0 && ev.neuronId < nNeurons) {
        fired[ev.neuronId] = true;
      }
    }
    return this.update(fired, dt);
  }

  /** Get current envelope (reference — don't mutate) */
  getEnvelope(): Float32Array { return this.envelope; }

  /** Aggregate: overall smoothed activity level (0-1) */
  getOverallActivity(): number {
    let sum = 0;
    for (let i = 0; i < this.envelope.length; i++) sum += this.envelope[i];
    return sum / this.envelope.length;
  }

  /** Per-group envelopes (e.g., excitatory vs inhibitory) */
  getGroupEnvelopes(groupSize: number): Float32Array {
    const nGroups = Math.ceil(this.envelope.length / groupSize);
    const out = new Float32Array(nGroups);
    for (let g = 0; g < nGroups; g++) {
      let sum = 0;
      let count = 0;
      for (let i = g * groupSize; i < Math.min((g + 1) * groupSize, this.envelope.length); i++) {
        sum += this.envelope[i];
        count++;
      }
      out[g] = count > 0 ? sum / count : 0;
    }
    return out;
  }

  reset(): void { this.envelope.fill(0); }
}

// ─── Output Types ────────────────────────────────────────────────────────────

export interface SonificationParams {
  pitchShift: number;
  amplitude: number;
  grainDensity: number;
  reverbMix: number;
  warmth: number;
  regularity: number;
  spikes: SpikeEvent[];
}
